import React, {useState, useEffect} from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Snackbar,
  Alert
} from "@mui/material";
import {
  GetApp as InstallIcon,
  Close as CloseIcon,
  PhoneAndroid as MobileIcon,
  Computer as DesktopIcon
} from "@mui/icons-material";
import {canInstallApp, handleAppInstall, isAppInstalled} from "../utils/pwaUtils";

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  useEffect(() => {
    // Check if app is already installed
    if (isAppInstalled()) {
      return;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = e => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowInstallPrompt(false);
      setSnackbarMessage("App installed successfully!");
      setSnackbarSeverity("success");
      setShowSnackbar(true);
    };

    if (canInstallApp()) {
      window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.addEventListener("appinstalled", handleAppInstalled);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        const result = await handleAppInstall(deferredPrompt);
        if (result) {
          setShowInstallPrompt(false);
          setSnackbarMessage("Installation started!");
          setSnackbarSeverity("success");
        } else {
          setSnackbarMessage("Installation was cancelled");
          setSnackbarSeverity("info");
        }
        setShowSnackbar(true);
        setDeferredPrompt(null);
      } catch (error) {
        setSnackbarMessage("Installation failed. Please try again.");
        setSnackbarSeverity("error");
        setShowSnackbar(true);
      }
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    setDeferredPrompt(null);
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  if (!showInstallPrompt) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          width: "90%",
          maxWidth: 400
        }}
      >
        <Card
          elevation={8}
          sx={{
            background: "linear-gradient(135deg, #1A73E8 0%, #4285F4 100%)",
            color: "white",
            borderRadius: 2
          }}
        >
          <CardContent sx={{p: 2}}>
            <Box sx={{display: "flex", alignItems: "center", mb: 2}}>
              <Box sx={{mr: 2}}>
                {navigator.userAgent.includes("Mobile") ? (
                  <MobileIcon sx={{fontSize: 32, color: "#FFFFFF"}} />
                ) : (
                  <DesktopIcon sx={{fontSize: 32, color: "#FFFFFF"}} />
                )}
              </Box>
              <Box sx={{flex: 1}}>
                <Typography variant="h6" sx={{fontWeight: 600, color: "#FFFFFF"}}>
                  Install HRM Portal
                </Typography>
                <Typography variant="body2" sx={{opacity: 0.9, color: "#FFFFFF"}}>
                  Get quick access to your HRM Portal with our app
                </Typography>
              </Box>
              <IconButton onClick={handleDismiss} sx={{color: "white"}} size="small">
                <CloseIcon sx={{color: "white !important"}} />
              </IconButton>
            </Box>

            <Box sx={{display: "flex", gap: 1}}>
              <Button
                variant="contained"
                startIcon={<InstallIcon />}
                onClick={handleInstallClick}
                sx={{
                  flex: 1,
                  bgcolor: "white",
                  color: "rgba(255, 255, 255, 0.9)",
                  border: "1px solid white"
                }}
              >
                Install
              </Button>
              <Button
                variant="outlined"
                onClick={handleDismiss}
                sx={{
                  borderColor: "rgba(255, 255, 255, 0.5)",
                  color: "white",
                  border: "1px solid white"
                }}
              >
                Later
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{vertical: "top", horizontal: "center"}}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{width: "100%"}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PWAInstallPrompt;
